import { ColumnsType } from '@seiue/ui'
import { isArray } from '@seiue/util'
import { Tooltip } from 'antd'
import humps from 'humps'
import styled from 'styled-components'

import {
  FormConfig,
  FormItemConfig,
  ISearchFormItemType,
  paramsFormat,
} from 'packages/components/DataTable'
import {
  ChildField,
  ChildFieldRes,
  ChildFieldResColumn,
  FieldGroupRes,
  FieldRes,
  FieldRespond,
  FieldTypeEnum,
  Guardian,
  InformalReflection,
  QuestionnaireResColumn,
  ReflectionIdcardTypeEnum,
  ReflectionPupilTypeEnum,
  TermReq,
} from 'packages/sdks-next/growp'
import { AnyType, AnyValueObj } from 'packages/types'
import { InfoTypeEnumToSearchFormType } from 'packages/utils'

import { ChildFieldResColumnWithMetaData, FieldResWithMetaData } from '@/uikit'
import { IGroupItem } from '@/uikit/MultiExport'

import { ColumnsRender } from './ColumnsRender'
import { guardianTypeToLabel, idcardTypeToLabel } from './dataMap'
import {
  accurateSearchKeys,
  guardiansFixedKeys,
  notEditableKeys,
  optShowLabel,
} from './info'

/**
 * URL字符串在页面中显示
 *
 * @param str - 任意字符串
 * @param type - 类型
 * @returns 显示结果
 */
export const urlRender = (str: string, type?: FieldTypeEnum) => {
  if (type === FieldTypeEnum.Date) {
    return str ? str.slice(0, 10) : str
  }
  const urlPattern =
    /^(((ht|f)tps?):\/\/)?([^!@#$%^&*?.\s-]([^!@#$%^&*?.\s]{0,63}[^!@#$%^&*?.\s])?\.)+[a-z]{2,6}\/?/
  if (urlPattern.test(str)) {
    return (
      <a
        target="_blank"
        rel="noreferrer"
        href={str}
      >
        {str}
      </a>
    )
  }
  return str
}

/**
 * 获取子表表头
 *
 * @param data - 子表表头相关信息
 * @param isIdCard - 是否是身份证
 * @returns 子表表头
 */
export const getChildrenColumn = (
  data: ChildFieldRes[] | QuestionnaireResColumn[],
  isIdCard = false
) => {
  let childrenColumn: ColumnsType<AnyType> = []
  if (isIdCard) {
    childrenColumn = [
      {
        title: '证件类型',
        key: 'idcard_type',
        dataIndex: 'idcard_type',
      },
      {
        title: '证件号',
        key: 'idcard',
        dataIndex: 'idcard',
      },
    ]
  } else {
    childrenColumn = data.map((i) => {
      return {
        title: (
          <Tooltip
            placement="topLeft"
            title={i.label}
            overlayStyle={{
              minWidth: '50px',
            }}
          >
            {i.label}
          </Tooltip>
        ),
        key: i.name,
        dataIndex: i.name,
        render: (text) => {
          return text ? (
            <DataRenderWrapper>
              <ColumnsRender
                field={i as ChildFieldRes}
                text={text}
              />
            </DataRenderWrapper>
          ) : (
            text
          )
        },
      }
    })
  }
  childrenColumn.unshift({
    title: '序号',
    render: (t, r, i) => `${i + 1}`,
  })
  return childrenColumn
}

/**
 * 获取子表信息
 *
 * @param record - 父表当前行数据
 * @param parentCol - 父表表头相关信息
 * @returns 子表数据
 */
export const getChildrenDataSource = (
  record: InformalReflection,
  parentCol: FieldRespond
) => {
  if (parentCol.label === '证件') {
    return [
      {
        id: '1',
        // eslint-disable-next-line
        idcard_type: record.idcardType
          ? idcardTypeToLabel.get(record.idcardType)
          : record.idcardType,
        idcard: record.idcard,
      },
    ]
  }
  const fields = record.fields || {}
  try {
    const value = fields[parentCol.name as keyof object]
    if (Array.isArray(value)) {
      return value
    }
    return value
  } catch (error) {
    // eslint-disable-next-line
    console.error(error)
  }
}

/**
 * 生源池-列表数据格式化
 *
 * @param data - Inform-student API返回的数据
 * @returns 格式化后的表格数据
 */
export const formatInformStuDataSource = (data: InformalReflection[]) => {
  const dSource = data || []
  if (dSource) {
    const dataSource = dSource.map((d: InformalReflection) => {
      const obj: AnyValueObj = {}
      if (d.guardians) {
        d.guardians.map((g: Guardian) => {
          const guardianLevel = g['guardianLevel']
          for (const k in g) {
            if (k === 'fields' && g['fields']) {
              const fields: AnyValueObj = g['fields']
              Object.keys(fields).map((key) => {
                obj[`guardians.${guardianLevel}.${key}`] = fields[key]
              })
            } else {
              obj[`guardians.${guardianLevel}.${k}`] = g[k as keyof Guardian]
            }
          }
        })
      }
      if (d.fields) {
        const fields = d.fields
        Object.keys(fields).map((key) => {
          obj[key] = fields[key as keyof object]
        })
      }
      const finalObj = {
        ...d,
        ...obj,
      }
      return finalObj
    })
    return dataSource
  }
  return dSource
}

export const selectNewKey: { [key: string]: (val: string) => string } = {
  'guardians.0.name': () => 'guardians.0.nameLike',
  'guardians.1.name': () => 'guardians.1.nameLike',
  'guardians.0.phone': (val: string) =>
    /，|,/.test(val) ? 'guardians.0.phoneIn' : 'guardians.0.phoneLike',
  'guardians.1.phone': (val: string) =>
    /，|,/.test(val) ? 'guardians.1.phoneIn' : 'guardians.1.phoneLike',
}

/**
 * 生源池-筛选请求参数格式化
 *
 * @param params - 筛选表单提交的数据
 * @returns API接收的最终参数
 */
export const formatInformStuSearchParams = (params: AnyValueObj) => {
  let finalParams: AnyValueObj = { ...params }
  Object.keys(params).map((key) => {
    let deleteOriginKey = true
    if (accurateSearchKeys.includes(key)) {
      return
    } else if (/^guardians.(0|1)./.test(key)) {
      let replaceStr = guardiansFixedKeys.includes(key)
        ? 'guardians.0.'
        : 'guardians.0.field.'
      if (/^guardians.1./.test(key)) {
        replaceStr = guardiansFixedKeys.includes(key)
          ? 'guardians.1.'
          : 'guardians.1.field.'
      }
      let newKey = key.replace(/^guardians.(0|1)./, replaceStr)
      let value = params[key]
      if (selectNewKey[key]) {
        if (/.phone$/.test(key)) {
          value = value.replace(/，/g, ',')
        }
        newKey = selectNewKey[key](params[key])
        deleteOriginKey = true
      }
      const newParams = paramsFormat(newKey, value)
      finalParams = { ...finalParams, ...newParams }
      guardiansFixedKeys.includes(key) ? (deleteOriginKey = false) : null
    } else if (/^name|phone$/.test(key)) {
      const newKey = /，|,/.test(params[key]) ? `${key}In` : `${key}Like`
      finalParams[newKey] = params[key].replace(/，|,/g, ',')
    } else {
      const newParams = paramsFormat(
        key,
        params[key],
        !notEditableKeys.includes(key)
      )
      finalParams = { ...finalParams, ...newParams }
    }
    if (deleteOriginKey) {
      finalParams[key] = undefined
    }
  })
  return finalParams
}

/**
 * 生源池-導出列表Tag配置
 *
 * @param groups - 接口返回原始分組數據
 * @returns 最終Tag List數據
 */
export const getExportConfig = (
  groups: FieldGroupRes[] | null
): IGroupItem[] => {
  const finalGroups = groups ? groups : []
  const config = finalGroups.map((group: FieldGroupRes) => {
    const fields = group.fields || []
    const groupList = fields
      .filter((field: FieldResWithMetaData) => {
        const { originLabel } = field.metadata || {}
        if (/^avatar|account$/.test(originLabel || '')) {
          return false
        }
        return true
      })
      .map((field) => {
        return {
          label: field.label,
          key: field.name,
        }
      })
    return {
      groupLabel: group.name,
      groupList,
    }
  })
  return config
}

/**
 * 获取选项标签
 *
 * @param field - 字段
 * @param name - 字段名
 * @returns 标签
 */
const getOptionLabel = (
  field: ChildField | ChildFieldResColumn | FieldRes,
  name: string
) => {
  if (
    /^(guardians.0.pupil_type|guardians.1.pupil_type)$/.test(field.name || '')
  ) {
    return guardianTypeToLabel.get(name as ReflectionPupilTypeEnum)
  }
  if (/^idcard_type$/.test(field.name || '')) {
    return idcardTypeToLabel.get(name as ReflectionIdcardTypeEnum)
  }
  if (field.name && optShowLabel[field.name]) {
    return optShowLabel[field.name](name)
  }
  return name
}

/**
 * 获取表单配置
 *
 * @param field - 字段
 * @returns 配置
 */
export const getFormItemConfigs = (
  field: ChildFieldResColumnWithMetaData | FieldResWithMetaData
): FormItemConfig => {
  const { originLabel } = field.metadata || {}
  return {
    type: InfoTypeEnumToSearchFormType.get(field.type),
    label: field.label,
    name: originLabel || field.name,
    otherConfig: {
      options: field.options
        ?.filter((opt) => !opt.deletedAt)
        .map((opt: TermReq) => {
          return {
            label: getOptionLabel(field, opt.name) || opt.name,
            value: [
              'gender',
              'status',
              'idcardType',
              'guardians.0.pupilType',
              'guardians.1.pupilType',
            ].includes(humps.camelize(originLabel || ''))
              ? opt.name
              : `${opt.id}` || '',
          }
        }),
    },
  }
}

const DataRenderWrapper = styled.div`
  max-width: 120px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

/**
 * Multi，Single类型数据ValueToLabel
 *
 * @param data - 原始数据
 * @param type - 数据属于哪种类型
 * @param options - 可选选项
 * @returns 处理后的数据
 */
export const optionValueToLabel = (
  data: string[] | number[] | string,
  type: FieldTypeEnum,
  options?: TermReq[]
) => {
  const valueToLabel: { [key: number]: string } = {}
  if (options) {
    options.map((option) => {
      if (option.id) {
        valueToLabel[option.id] = option.name
      }
    })
  }
  if (type === FieldTypeEnum.Multi && isArray(data)) {
    return data.map((id) => {
      return valueToLabel[Number(id)] || id
    })
  }
  if (type === FieldTypeEnum.Single) {
    return valueToLabel[Number(data)] || data
  }
  return data
}

/**
 * 获取用户 datatable 筛选配置
 *
 * @param finalGroupsData - 字段组
 * @returns 配置
 */
export const getSearchFormConfig = (
  finalGroupsData: FieldGroupRes[]
): FormConfig => {
  const config: FormConfig = {}
  finalGroupsData.map((group) => {
    let itemList: FormItemConfig[] = []
    const groupFields = group.fields ? group.fields : []
    groupFields
      .filter((item: FieldRes) => {
        if (
          /^attachment$/.test(item.type) ||
          /^(avatar|account)$/.test(item.name) ||
          !item.isShow ||
          (item.type === FieldTypeEnum.Product && item.label !== '证件')
        ) {
          return false
        }
        return true
      })
      .map((field: FieldRes) => {
        if (field.type === 'product' && field.children) {
          if (field.label === '证件') {
            itemList = [
              ...itemList,
              ...[
                {
                  type: ISearchFormItemType.Select,
                  label: '证件类型',
                  name: 'idcard_type',
                  otherConfig: {
                    options: idcardTypeToLabel.listOptions('value', 'label'),
                  },
                },
                {
                  type: ISearchFormItemType.Input,
                  label: '证件号',
                  name: 'idcard',
                },
              ],
            ]
          } else {
            field.children.map((child) => {
              itemList.push(getFormItemConfigs(child))
            })
          }
        } else {
          itemList.push(getFormItemConfigs(field))
        }
      })
    config[group.name] = itemList
  })
  return config
}

/**
 * 对私信息格式化
 *  - 手机号：保留前三后二，hover 显示
 *  - 账号：全 *，hover 显示
 *  - 证件号：全 *，hover 显示
 *
 * @param info - 信息
 * @param allHidden - 是否全部隐藏，默认是
 * @param onlyText - 是否只显示文本
 */
export const formatPrivateInfo = (
  info?: string | null,
  allHidden = true,
  onlyText = false
) => {
  if (onlyText) {
    return info ? hiddenContent(info, allHidden) : ''
  }
  if (!info) {
    return '-'
  }

  const content = hiddenContent(info, allHidden)

  return (
    <PrivateInfo>
      <div>{content}</div>
      <span>{info}</span>
    </PrivateInfo>
  )
}

/**
 * 对私信息格式化内容
 *
 * @param info - 信息
 * @param allHidden - 是否全部隐藏，默认是
 */
const hiddenContent = (info: string, allHidden: boolean) =>
  allHidden
    ? '*'.repeat(info.length)
    : info.replace(/(\d{3})\d{6}(\d{2})/, '$1******$2')

const PrivateInfo = styled.div`
  span {
    display: none;
  }

  &:hover {
    span {
      display: block;
    }
    div {
      display: none;
    }
  }
`
